import { AudioWorkletNodeSynthesizer } from 'js-synthesizer';
import type { ISequencer } from 'js-synthesizer';
import type { Synth, SynthProgress } from './synth';
import { FLUIDSYNTH_JS_URL, JSSYNTH_WORKLET_URL } from '../constants';

export class JsSynthesizerSynth implements Synth {
  readonly backend = 'audioworklet' as const;
  private synth: AudioWorkletNodeSynthesizer | null = null;
  private node: AudioWorkletNode | null = null;
  private gain: GainNode | null = null;
  private ctx: AudioContext | null = null;
  private seq: ISequencer | null = null;
  private seqClientId = -1;
  private currentSfontId: number | null = null;

  async init(ctx: AudioContext, onProgress?: SynthProgress): Promise<void> {
    this.ctx = ctx;
    onProgress?.('load-fluidsynth');
    await ctx.audioWorklet.addModule(FLUIDSYNTH_JS_URL);
    onProgress?.('load-worklet');
    await ctx.audioWorklet.addModule(JSSYNTH_WORKLET_URL);
    onProgress?.('create-node');
    const synth = new AudioWorkletNodeSynthesizer();
    synth.init(ctx.sampleRate);
    const node = synth.createAudioNode(ctx);
    const gain = ctx.createGain();
    gain.gain.value = 1;
    node.connect(gain);
    gain.connect(ctx.destination);
    this.synth = synth;
    this.node = node;
    this.gain = gain;

    onProgress?.('create-sequencer');
    const seq = await synth.createSequencer();
    seq.setTimeScale(1000);
    this.seqClientId = await seq.registerSynthesizer(synth);
    this.seq = seq;
  }

  async loadSoundFont(sf2: ArrayBuffer, onProgress?: SynthProgress): Promise<string> {
    if (!this.synth) throw new Error('synth not initialized');
    onProgress?.('load-soundfont');
    const sfontId = await this.synth.loadSFont(sf2);
    const sfont = await this.synth.getSFontObject(sfontId);
    const presets = Array.from(await sfont.getPresetIterable());
    const first = presets[0];
    if (!first) {
      // Bad/empty font: unload it and keep whatever was previously bound.
      await this.synth.unloadSFontAsync(sfontId).catch(() => {});
      throw new Error('SF2 contains no presets');
    }
    const name = sfont.getName();
    console.log(
      `[synth] SF2 "${name}" presets:`,
      presets.map((p) => `${p.bankNum}:${p.num} ${p.name}`),
    );
    this.synth.midiProgramSelect(0, sfontId, first.bankNum, first.num);
    console.log(`[synth] bound channel 0 → ${first.bankNum}:${first.num} ${first.name}`);
    // Release the previously loaded font now that channel 0 points at the new one.
    if (this.currentSfontId !== null && this.currentSfontId !== sfontId) {
      await this.synth.unloadSFontAsync(this.currentSfontId).catch(() => {});
    }
    this.currentSfontId = sfontId;
    onProgress?.('ready');
    return name;
  }

  noteOn(channel: number, key: number, velocity: number): void {
    this.synth?.midiNoteOn(channel, key, velocity);
  }
  noteOff(channel: number, key: number): void {
    this.synth?.midiNoteOff(channel, key);
  }
  allNotesOff(channel: number = 0): void {
    this.synth?.midiAllNotesOff(channel);
  }
  programChange(channel: number, program: number): void {
    this.synth?.midiProgramChange(channel, program);
  }

  scheduleNote(channel: number, key: number, velocity: number, deltaMs: number): void {
    if (!this.seq) return;
    const tick = Math.max(0, Math.round(deltaMs));
    // Match iOS exactly: emit MIDI 0x90 (note-on) for both on and off, vel=0 for off.
    this.seq.sendEventAt({ type: 'noteon', channel, key, vel: velocity }, tick, false);
  }

  clearScheduled(): void {
    this.seq?.removeAllEvents();
  }

  setMasterGain(value: number): void {
    if (!this.gain || !this.ctx) return;
    const v = Math.max(0, Math.min(2, value));
    this.gain.gain.setTargetAtTime(v, this.ctx.currentTime, 0.02);
  }

  async dispose(): Promise<void> {
    try {
      this.seq?.close();
      this.synth?.midiAllNotesOff();
      this.node?.disconnect();
      this.gain?.disconnect();
      this.synth?.close();
    } catch {
      /* ignore */
    }
    this.seq = null;
    this.synth = null;
    this.node = null;
    this.gain = null;
    this.ctx = null;
  }
}
